import React, { createContext, useContext, useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { startOfMonth, subMonths, endOfMonth, addMonths, format } from 'date-fns';
import * as R from 'ramda';

import { DEFAULT_CALENDAR_VIEW, useCalendarData, useCalendarSettings } from '..';
import { parseQueryParams, updateQueryParams } from './utils';
import {
	filterByCity,
	filterByCountry,
	filterByEventTags,
	filterByEventType,
	filterByEventVenue,
	filterBySearchTerm,
	filterByState,
} from './filters';

import type { IEvent } from '..';

export interface Filters {
	selectedEventTypes: string[];
	selectedEventTags: string[];
	selectedEventLocations: string[];
	selectedCountries: string[];
	selectedStates: string[];
	selectedCities: string[];
}

export interface DefaultFilters {
	eventTypes: string[];
	eventTags: string[];
	eventLocations: string[];
	countries: string[];
	states: string[];
	cities: string[];
}

interface FiltersContextProps {
	events: IEvent[];
	filteredEvents: IEvent[];
	defaultFilters: {
		eventTypes: string[];
		eventTags: string[];
		eventLocations: string[];
		countries: string[];
		states: string[];
		cities: string[];
	};
	filters: Filters;
	searchTerm: string;
	currentView: string;
	currentMonth: string;
	filtersInitializedRef: React.MutableRefObject<boolean>;
	setEvents: React.Dispatch<React.SetStateAction<IEvent[]>>;
	setFilters: React.Dispatch<React.SetStateAction<Filters>>;
	setFilteredEvents: React.Dispatch<React.SetStateAction<IEvent[]>>;
	setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
	setCurrentView: React.Dispatch<React.SetStateAction<string>>;
	setCurrentMonth: React.Dispatch<React.SetStateAction<string>>;
	handleApplyFilters: () => void;
}

const FiltersContext = createContext<FiltersContextProps | undefined>(undefined);

const withFilter = (predicate) => R.filter(predicate);

const applyEventFilters = (events, filters, searchTerm) =>
	R.pipe(
		withFilter(filterBySearchTerm(searchTerm)),
		withFilter(filterByCountry(filters)),
		withFilter(filterByState(filters)),
		withFilter(filterByCity(filters)),
		withFilter(filterByEventType(filters)),
		withFilter(filterByEventTags(filters)),
		withFilter(filterByEventVenue(filters))
	)(events);

export const FiltersProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { searchTerm: initialSearchTerm, filters: initialFilters } = parseQueryParams();
	const { eventsBySource, fetchAllEventsInRange, getAllEvents } = useCalendarData();
	const [defaultFilters, setDefaultFilters] = useState<DefaultFilters>({
		eventTypes: [],
		eventTags: [],
		eventLocations: [],
		countries: [],
		states: [],
		cities: [],
	});
	const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
	const [filters, setFilters] = useState<Filters>(initialFilters);
	const [events, setEvents] = useState<IEvent[] | null>(null);
	const [filteredEvents, setFilteredEvents] = useState<IEvent[] | null>(null);
	const filtersInitializedRef = useRef(false);

	const { settings } = useCalendarSettings();
	const [currentView, setCurrentView] = useState<string>(settings?.defaultView || DEFAULT_CALENDAR_VIEW);
	const [currentMonth, setCurrentMonth] = useState();

	useEffect(() => {
		setCurrentView(settings?.defaultView || DEFAULT_CALENDAR_VIEW);
	}, [settings]);

	useEffect(() => {
		const loadInitialEvents = async () => {
			const now = new Date();
			const start = format(startOfMonth(subMonths(now, 1)), 'yyyy-MM-dd');
			const end = format(endOfMonth(addMonths(now, 1)), 'yyyy-MM-dd');
			await fetchAllEventsInRange({ start, end });
		};

		loadInitialEvents();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const events = getAllEvents();
		if (events) {
			setEvents(events);
			if (!filtersInitializedRef.current) {
				setFilteredEvents(events);
			}
		}
	}, [eventsBySource, getAllEvents]);

	useEffect(() => {
		if (events?.length > 0) {
			const eventTypes = [...new Set(events.flatMap((event) => event.eventType))].filter((event) => event);
			const eventTags = [...new Set(events.flatMap((event) => event.tags))].filter((event) => event);
			const eventLocations = [...new Set(events.flatMap((event) => event.venue))].filter((event) => event);

			const countries = Array.from(new Set(events.map((event) => event.country).filter(Boolean)));
			const states = Array.from(new Set(events.map((event) => event.state).filter(Boolean)));
			const cities = Array.from(new Set(events.map((event) => event.city).filter(Boolean)));

			setDefaultFilters({ eventTypes, eventTags, eventLocations, countries, states, cities });
		}
	}, [events]);

	const handleApplyFilters = useCallback(() => {
		setFilters((filters) => {
			const modifiedFilters = filters;
			setSearchTerm((searchTerm) => {
				const filteredEvents = applyEventFilters(events, modifiedFilters, searchTerm);
				setFilteredEvents(filteredEvents as IEvent[]);
				updateQueryParams(searchTerm, filters);
				return searchTerm;
			});
			if (modifiedFilters.selectedCountries.length === 0) {
				modifiedFilters.selectedCities = [];
				modifiedFilters.selectedStates = [];
			}
			if (modifiedFilters.selectedStates.length === 0) {
				modifiedFilters.selectedCities = [];
			}
			return modifiedFilters;
		});
	}, [events, setFilteredEvents, setFilters, setSearchTerm]);

	useEffect(() => {
		if (Object.values(defaultFilters).some((arr) => arr.length > 0)) {
			if (
				!filtersInitializedRef.current &&
				(initialSearchTerm || Object.values(initialFilters).some((arr) => arr.length > 0))
			) {
				handleApplyFilters();
				filtersInitializedRef.current = true;
			}
		}
	}, [events, defaultFilters, initialSearchTerm, initialFilters, handleApplyFilters]);

	useEffect(() => {
		if (filtersInitializedRef.current && events?.length > 0) {
			handleApplyFilters();
		}
	}, [events, handleApplyFilters]);

	const contextValue = useMemo(() => {
		return {
			events: events || [],
			filteredEvents: filteredEvents || [],
			defaultFilters,
			filters,
			searchTerm,
			currentView,
			currentMonth,
			filtersInitializedRef,
			setEvents,
			setFilters,
			setFilteredEvents,
			setSearchTerm,
			setCurrentView,
			setCurrentMonth,
			handleApplyFilters,
		};
	}, [events, filteredEvents, defaultFilters, filters, searchTerm, currentView, currentMonth, handleApplyFilters]);

	return <FiltersContext.Provider value={contextValue}>{children}</FiltersContext.Provider>;
};

export const useFilters = (): FiltersContextProps => {
	const context = useContext(FiltersContext);
	if (!context) {
		throw new Error('useFilters must be used within a FiltersProvider');
	}
	return context;
};
